import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { CompensationMethod } from "../return";
import { StepType } from "./common";

const baseStepFields = {
  customTitle: z.string().optional(),
  type: createEnum(StepType),
  id: z.string(),
};

const compensationStepSchema_ = z.object({
  ...baseStepFields,
  type: z.literal(StepType.COMPENSATION_STEP),
  nextId: z.string().nullish(),
  /** Compensation methods this step is allowed to process. */
  compensationMethods: z
    .array(createEnum(CompensationMethod, { enumName: "CompensationMethod" }))
    .nullish(),
  /** Whether to send a customer notification when this step runs. */
  notifyCustomer: z.boolean().nullish(),
  /** Free-form note attached to the customer-facing communication. */
  customerNotes: z.string().nullish(),
});

export const compensationStepSchema: z.ZodType<CompensationStep> =
  compensationStepSchema_;
export interface CompensationStep
  extends z.infer<typeof compensationStepSchema_> {}

const restockingStepSchema_ = z.object({
  ...baseStepFields,
  type: z.literal(StepType.RESTOCKING_STEP),
  nextId: z.string().nullish(),
  /**
   * Per-step override of `team.settings.inventory.restockDamaged`. Tri-state:
   * undefined/null = inherit team setting, true = restock damaged items,
   * false = skip damaged items. The per-product
   * `pendingProcessingInfo.shouldRestock` still takes precedence over both.
   * Editor UI for this field lands as a follow-up.
   */
  inventory: z.object({ restockDamaged: z.boolean().nullish() }).nullish(),
});

export const restockingStepSchema: z.ZodType<RestockingStep> =
  restockingStepSchema_;
export interface RestockingStep extends z.infer<typeof restockingStepSchema_> {}

export const CompensationPhase = [
  "approval",
  "in_transit",
  "delivered",
] as const;
export type CompensationPhase = (typeof CompensationPhase)[number];

/**
 * Branch step for Compensation/Restocking flows that wraps the editor's
 * existing return-flow `Condition` discriminated union (see
 * redo/model/src/return-flow/condition.ts). The compensation worker
 * evaluates the embedded condition at run-time via the shared
 * `Condition.evaluate(...)` switch, so no per-condition translation is
 * required at the persistence boundary.
 *
 * The return-flow `Condition` union is described via the older JsonFormat
 * library rather than Zod, so we accept the field here as passthrough JSON.
 * Runtime safety is delegated to `Condition.evaluate`, which already
 * handles every supported condition kind.
 */
const returnFlowConditionStepSchema_ = z.object({
  ...baseStepFields,
  type: z.literal(StepType.RETURN_FLOW_CONDITION),
  /** Next step when the condition evaluates to true. */
  nextTrueId: z.string().nullish(),
  /** Next step when the condition evaluates to false. */
  nextFalseId: z.string().nullish(),
  /** Return-flow `Condition` discriminated union payload. */
  condition: z.record(z.string(), z.unknown()),
});

export const returnFlowConditionStepSchema: z.ZodType<ReturnFlowConditionStep> =
  returnFlowConditionStepSchema_;
export interface ReturnFlowConditionStep
  extends z.infer<typeof returnFlowConditionStepSchema_> {}
